import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { MdStar, MdStarBorder, MdStarHalf } from 'react-icons/md';
import {
  AppCard,
  OutlineButton,
  SortableTh,
  TableLoader,
} from '../../../../components/ui';
import { getBuSiteSummary } from '../../../../repositories/dashboard.repository';
import { useTableSort } from '../../../../hooks/useTableSort';

function AvgStarRating({ value }) {
  if (value == null || Number.isNaN(Number(value))) {
    return (
      <Text as="span" color="text.secondary" fontSize="15px">
        —
      </Text>
    );
  }
  const rating = Math.max(0, Math.min(5, Number(value)));
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.3 && rating - full < 0.8;
  const roundedHalf = rating - full >= 0.8 ? 1 : 0;
  const filled = full + roundedHalf;
  return (
    <HStack spacing={0.5} title={`${rating.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => {
        let icon = MdStarBorder;
        if (n <= filled) icon = MdStar;
        else if (n === filled + 1 && hasHalf && !roundedHalf) icon = MdStarHalf;
        return (
          <Icon
            key={n}
            as={icon}
            boxSize="17px"
            color={icon === MdStarBorder ? 'gray.300' : 'brand.500'}
          />
        );
      })}
      <Text fontSize="13px" color="text.secondary" ml={1}>
        {rating.toFixed(1)}
      </Text>
    </HStack>
  );
}

function CellDash({ value }) {
  if (value == null || value === '') {
    return (
      <Text as="span" color="text.secondary">
        —
      </Text>
    );
  }
  return value;
}

export default function BuSiteSummaryPage() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAllBusinessUnits, setShowAllBusinessUnits] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBuSiteSummary({ includeEmpty: showAllBusinessUnits });
      setRows(data);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load BU / Site summary',
        status: 'error',
        position: 'top-right',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [toast, showAllBusinessUnits]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.buName.toLowerCase().includes(q) ||
        r.buCode.toLowerCase().includes(q) ||
        r.siteName.toLowerCase().includes(q) ||
        r.siteCode.toLowerCase().includes(q) ||
        String(r.projectOperation || '')
          .toLowerCase()
          .includes(q),
    );
  }, [rows, search]);

  const { sorted, sort, toggleSort } = useTableSort(
    filtered,
    {
      buName: (r) => r.buName,
      siteName: (r) => r.siteName,
      projectOperation: (r) => r.projectOperation || '',
      requestTotal: (r) => r.requestTotal,
      requestPending: (r) => r.requestPending,
      requestRecommended: (r) => r.requestRecommended,
      requestAssigned: (r) => r.requestAssigned,
      requestReleased: (r) => r.requestReleased,
      requestCancelled: (r) => r.requestCancelled,
      deploymentCount: (r) => r.deploymentCount,
      releaseCount: (r) => r.releaseCount,
      activeDeployments: (r) => r.activeDeployments,
      avgRating: (r) => (r.avgRating == null ? -1 : r.avgRating),
      avgTurnaroundDays: (r) =>
        r.avgTurnaroundDays == null ? -1 : r.avgTurnaroundDays,
      vendorTotal: (r) => r.vendorTotal,
      vendorPending: (r) => r.vendorPending,
      vendorVerified: (r) => r.vendorVerified,
      equipmentAvailable: (r) => r.equipmentAvailable,
    },
    { key: 'buName', dir: 'asc' },
  );

  return (
    <>
      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        gap={3}
        mb={2}
        wrap="wrap"
      >
        <Box>
          <Heading size="lg">BU / Site summary</Heading>
          <Text color="text.secondary" mt={1}>
            Requests, deployments, vendors, and availability by Business Unit site.
            {showAllBusinessUnits
              ? ' Showing all sites in scope, including zeros.'
              : ' Empty sites are hidden unless you show all.'}
          </Text>
        </Box>
        <OutlineButton onClick={load} isDisabled={loading}>
          Refresh
        </OutlineButton>
      </Flex>

      <AppCard mb={4}>
        <Flex
          align={{ base: 'stretch', md: 'flex-end' }}
          gap={4}
          wrap="wrap"
        >
          <Box flex="1" minW={{ base: '100%', sm: '260px' }} maxW="420px">
            <FormLabel fontSize="13px" mb={1}>
              Filter sites
            </FormLabel>
            <Input
              placeholder="Search by BU, site, code, or project…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Checkbox
            isChecked={showAllBusinessUnits}
            onChange={(e) => setShowAllBusinessUnits(e.target.checked)}
            mb={{ base: 0, md: 2 }}
            alignItems="center"
            whiteSpace="nowrap"
          >
            <Text fontSize="14px">Show all Business Units</Text>
          </Checkbox>
        </Flex>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Flex
          px={5}
          py={3}
          borderBottomWidth="1px"
          borderColor="border.subtle"
          align="center"
        >
          <Text fontSize="13px" color="text.secondary">
            {loading
              ? 'Loading…'
              : showAllBusinessUnits
                ? `${sorted.length} row${sorted.length === 1 ? '' : 's'} (all BUs / sites in scope)`
                : `${sorted.length} site${sorted.length === 1 ? '' : 's'} with data`}
          </Text>
        </Flex>
        {loading ? (
          <TableLoader rows={6} cols={10} />
        ) : (
          <Box overflowX="auto">
            <Table
              variant="simple"
              size="sm"
              sx={{
                th: {
                  py: '10px',
                  px: '12px',
                  fontSize: '13px',
                  lineHeight: '1.25',
                  whiteSpace: 'nowrap',
                },
                td: {
                  py: '12px',
                  px: '12px',
                  fontSize: '15px',
                  lineHeight: '1.35',
                  verticalAlign: 'middle',
                },
              }}
            >
              <Thead>
                <Tr>
                  <Th
                    colSpan={3}
                    textAlign="center"
                    bg="blue.50"
                    color="blue.800"
                    borderBottomWidth="1px"
                    borderColor="blue.100"
                    py="8px"
                    fontSize="12px"
                    letterSpacing="0.06em"
                    _dark={{ bg: 'blue.900', color: 'blue.100', borderColor: 'blue.700' }}
                  >
                    Site
                  </Th>
                  <Th
                    colSpan={6}
                    textAlign="center"
                    bg="orange.50"
                    color="orange.800"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="orange.100"
                    py="8px"
                    fontSize="12px"
                    letterSpacing="0.06em"
                    _dark={{ bg: 'orange.900', color: 'orange.100', borderColor: 'orange.700' }}
                  >
                    Indent section
                  </Th>
                  <Th
                    colSpan={6}
                    textAlign="center"
                    bg="green.50"
                    color="green.800"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="green.100"
                    py="8px"
                    fontSize="12px"
                    letterSpacing="0.06em"
                    _dark={{ bg: 'green.900', color: 'green.100', borderColor: 'green.700' }}
                  >
                    Equipment section
                  </Th>
                  <Th
                    colSpan={3}
                    textAlign="center"
                    bg="purple.50"
                    color="purple.800"
                    borderBottomWidth="1px"
                    borderLeftWidth="1px"
                    borderColor="purple.100"
                    py="8px"
                    fontSize="12px"
                    letterSpacing="0.06em"
                    _dark={{ bg: 'purple.900', color: 'purple.100', borderColor: 'purple.700' }}
                  >
                    Vendor section
                  </Th>
                </Tr>
                <Tr bg="bg.input">
                  <SortableTh columnKey="buName" sort={sort} onSort={toggleSort} minW="140px">
                    Business Unit
                  </SortableTh>
                  <SortableTh
                    columnKey="siteName"
                    sort={sort}
                    onSort={toggleSort}
                    minW="180px"
                  >
                    Site
                  </SortableTh>
                  <SortableTh
                    columnKey="projectOperation"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Project
                  </SortableTh>
                  <SortableTh
                    columnKey="requestTotal"
                    sort={sort}
                    onSort={toggleSort}
                    borderLeftWidth="1px"
                    borderColor="border.subtle"
                  >
                    Requests
                  </SortableTh>
                  <SortableTh
                    columnKey="requestPending"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Pending
                  </SortableTh>
                  <SortableTh
                    columnKey="requestRecommended"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Recommended
                  </SortableTh>
                  <SortableTh
                    columnKey="requestAssigned"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Assigned
                  </SortableTh>
                  <SortableTh
                    columnKey="requestReleased"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Released
                  </SortableTh>
                  <SortableTh
                    columnKey="requestCancelled"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Cancelled
                  </SortableTh>
                  <SortableTh
                    columnKey="deploymentCount"
                    sort={sort}
                    onSort={toggleSort}
                    borderLeftWidth="1px"
                    borderColor="border.subtle"
                  >
                    Deployments
                  </SortableTh>
                  <SortableTh
                    columnKey="activeDeployments"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Live now
                  </SortableTh>
                  <SortableTh
                    columnKey="releaseCount"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Releases
                  </SortableTh>
                  <SortableTh columnKey="avgRating" sort={sort} onSort={toggleSort}>
                    Avg rating
                  </SortableTh>
                  <SortableTh
                    columnKey="avgTurnaroundDays"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Avg days
                  </SortableTh>
                  <SortableTh
                    columnKey="equipmentAvailable"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Equip. avail.
                  </SortableTh>
                  <SortableTh
                    columnKey="vendorTotal"
                    sort={sort}
                    onSort={toggleSort}
                    borderLeftWidth="1px"
                    borderColor="border.subtle"
                  >
                    Vendors
                  </SortableTh>
                  <SortableTh
                    columnKey="vendorPending"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Vendor KYC
                  </SortableTh>
                  <SortableTh
                    columnKey="vendorVerified"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Verified
                  </SortableTh>
                </Tr>
              </Thead>
              <Tbody>
                {sorted.map((r) => (
                  <Tr key={r.siteId ?? `bu-${r.buId}`}>
                    <Td minW="140px">
                      <Text fontWeight="600" fontSize="15px" whiteSpace="nowrap">
                        {r.buName || '—'}
                      </Text>
                      {r.buCode ? (
                        <Text
                          fontSize="13px"
                          color="text.secondary"
                          fontFamily="mono"
                          whiteSpace="nowrap"
                        >
                          {r.buCode}
                        </Text>
                      ) : null}
                    </Td>
                    <Td minW="180px">
                      <Text fontWeight="600" fontSize="15px" whiteSpace="nowrap">
                        {r.siteName || 'No site'}
                      </Text>
                      {r.siteCode ? (
                        <Text
                          fontSize="13px"
                          color="text.secondary"
                          fontFamily="mono"
                          whiteSpace="nowrap"
                        >
                          {r.siteCode}
                        </Text>
                      ) : (
                        <Text fontSize="13px" color="text.secondary">
                          Add a site under this BU
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <CellDash value={r.projectOperation || null} />
                    </Td>
                    <Td
                      fontWeight="600"
                      borderLeftWidth="1px"
                      borderColor="border.subtle"
                    >
                      {r.requestTotal}
                    </Td>
                    <Td>{r.requestPending}</Td>
                    <Td>{r.requestRecommended}</Td>
                    <Td>{r.requestAssigned}</Td>
                    <Td>{r.requestReleased}</Td>
                    <Td>{r.requestCancelled}</Td>
                    <Td borderLeftWidth="1px" borderColor="border.subtle">
                      {r.deploymentCount}
                    </Td>
                    <Td fontWeight="600">{r.activeDeployments}</Td>
                    <Td>{r.releaseCount}</Td>
                    <Td>
                      <AvgStarRating value={r.avgRating} />
                    </Td>
                    <Td>
                      {r.avgTurnaroundDays == null ? (
                        <Text as="span" color="text.secondary">
                          —
                        </Text>
                      ) : (
                        r.avgTurnaroundDays
                      )}
                    </Td>
                    <Td>{r.equipmentAvailable}</Td>
                    <Td borderLeftWidth="1px" borderColor="border.subtle">
                      {r.vendorTotal}
                    </Td>
                    <Td>{r.vendorPending}</Td>
                    <Td>{r.vendorVerified}</Td>
                  </Tr>
                ))}
                {sorted.length === 0 ? (
                  <Tr>
                    <Td colSpan={18}>
                      <Text color="text.secondary" py={8} textAlign="center">
                        No sites with requests, vendors, or activity yet.
                      </Text>
                    </Td>
                  </Tr>
                ) : null}
              </Tbody>
            </Table>
          </Box>
        )}
      </AppCard>
    </>
  );
}
